import { useRef, useEffect, useCallback, useState } from "react";
import emailjs from "@emailjs/browser";
import { WidgetInstance } from "friendly-challenge";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { Form, Input, Button, Checkbox, notification } from "antd";

const { TextArea } = Input;

// Runtime-assembled to prevent static crawlers from harvesting the address
const at = String.fromCharCode(64);
const EMAIL_CONTACT = ["info", "saxemble.de"].join(at);

// Replace with your real sitekey from https://app.friendlycaptcha.com
// For local development the widget runs in demo mode and always succeeds.
const FC_SITEKEY = process.env.REACT_APP_FC_SITEKEY || "FCMOII32Q8K0P42O";

function FriendlyCaptcha({ onDone, onError }) {
  const outerRef = useRef(null);

  useEffect(() => {
    const outer = outerRef.current;
    if (!outer) return;

    // Create a fresh inner div each run so destroy()'s el.remove() only removes
    // the inner element — never the outer div that React manages via outerRef.
    const inner = document.createElement("div");
    inner.className = "frc-captcha";
    inner.dataset.sitekey = FC_SITEKEY;
    outer.innerHTML = "";
    outer.appendChild(inner);

    let active = true;
    const widget = new WidgetInstance(inner, {
      startMode: "none",
      doneCallback: (...a) => {
        if (active) onDone(...a);
      },
      errorCallback: (...a) => {
        if (active) onError(...a);
      },
    });

    // Push start() past React StrictMode's sync cleanup+remount cycle.
    // In StrictMode cleanup fires before this timer → clearTimeout cancels it →
    // no workers start on the first mount → destroy() is race-condition-free.
    const timer = setTimeout(() => {
      if (active) widget.start();
    }, 0);

    return () => {
      active = false;
      clearTimeout(timer);
      widget.destroy();
    };
  }, [onDone, onError]);

  return <div ref={outerRef} className="friendlyCaptcha" />;
}

function AppContact() {
  const [form] = Form.useForm();
  const [captchaToken, setCaptchaToken] = useState(null);
  const [captchaError, setCaptchaError] = useState(false);
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [api, contextHolder] = notification.useNotification();

  const handleCaptchaDone = useCallback((token) => {
    setCaptchaToken(token);
    setCaptchaError(false);
  }, []);

  const handleCaptchaError = useCallback(() => {
    setCaptchaToken(null);
    setCaptchaError(true);
  }, []);

  const openNotificationSuccess = () => {
    api.success({
      message: "Nachricht erfolgreich gesendet",
      description:
        "Vielen Dank. Wir werden uns schnellstmöglich bei dir melden.",
    });
  };

  const openNotificationFail = () => {
    api.error({
      message: "Sendevorgang fehlgeschlagen",
      description: `Leider gibt es aktuell technische Schwierigkeiten. Alternativ kannst du uns gerne eine E-Mail senden: ${EMAIL_CONTACT}`,
    });
  };

  const sendMail = (values) => {
    if (!captchaToken) {
      setCaptchaError(true);
      return;
    }
    emailjs
      .send("service_wol6cu9", "template_3ycfvhh", values, "z6fcJ3OhmjaiEi7wB")
      .then(openNotificationSuccess, openNotificationFail);
    form.resetFields();
    setCaptchaToken(null);
  };

  return (
    <div id="contact" className="contactBlock">
      <div className="container-fluid">
        {contextHolder}
        <motion.div
          className="titleHolder"
          ref={ref}
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5 }}
        >
          <h2>Kontakt</h2>
          <p style={{ marginTop: 12, color: "#666", fontSize: 15 }}>
            Du hast Fragen oder möchtest bei uns mitspielen? Schreib uns einfach
            über das Kontaktformular.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 25 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.55, delay: 0.15 }}
        >
          <Form
            form={form}
            name="contact_form"
            layout="vertical"
            onFinish={sendMail}
          >
            <div
              style={{
                textAlign: "right",
                color: "#888",
                fontSize: "13px",
                marginBottom: "8px",
              }}
            >
              <span style={{ color: "#ff4d4f" }}>*</span> Pflichtfeld
            </div>

            <Form.Item
              name="fullname"
              label="Vor- und Nachname"
              rules={[
                {
                  required: true,
                  message: "Bitte gib deinen Vor- und Nachnamen ein",
                },
              ]}
            >
              <Input placeholder="Vor- und Nachname" size="large" />
            </Form.Item>

            <Form.Item
              name="email"
              label="E-Mail Adresse"
              rules={[
                {
                  type: "email",
                  message: "Die eingegebene E-Mail ist nicht gültig",
                },
                {
                  required: true,
                  message: "Bitte gib deine E-Mail Adresse ein",
                },
              ]}
            >
              <Input placeholder="E-Mail Adresse" size="large" />
            </Form.Item>

            <Form.Item name="telephone" label="Telefonnummer">
              <Input placeholder="Telefonnummer (optional)" size="large" />
            </Form.Item>

            <Form.Item name="subject" label="Betreff">
              <Input placeholder="Betreff (optional)" size="large" />
            </Form.Item>

            <Form.Item
              name="message"
              label="Nachricht"
              rules={[
                {
                  required: true,
                  message: "Bitte gib eine Nachricht ein",
                },
              ]}
            >
              <TextArea placeholder="Deine Nachricht..." size="large" />
            </Form.Item>

            <Form.Item
              name="conditions"
              valuePropName="checked"
              rules={[
                {
                  validator: (_, value) =>
                    value
                      ? Promise.resolve()
                      : Promise.reject(
                          "Bitte akzeptiere die Datenschutzerklärung"
                        ),
                },
              ]}
            >
              <Checkbox>
                <span style={{ color: "#ff4d4f", marginRight: 4 }}>*</span>
                Ich habe die Datenschutzerklärung zur Kenntnis genommen. Ich
                stimme zu, dass meine Angaben und Daten zur Beantwortung meiner
                Anfrage elektronisch erhoben und gespeichert werden. Hinweis:
                Sie können Ihre Einwilligung jederzeit für die Zukunft per
                E-Mail widerrufen.
              </Checkbox>
            </Form.Item>

            <Form.Item>
              <FriendlyCaptcha
                onDone={handleCaptchaDone}
                onError={handleCaptchaError}
              />
              {captchaError && (
                <div className="captchaError">
                  Bitte bestätige, dass du kein Bot bist.
                </div>
              )}
            </Form.Item>

            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                size="large"
                style={{ minWidth: 160 }}
              >
                Abschicken
              </Button>
            </Form.Item>
          </Form>
        </motion.div>
      </div>
    </div>
  );
}

export default AppContact;
